import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, Play, GanttChart, Sparkles, FileText, CalendarCheck, RotateCcw, Layers, TrendingUp, AlertTriangle, Lightbulb, ShieldCheck, Lock } from "lucide-react";
import { cn } from "@/lib/utils";
import { get, post, toastError } from "@/lib/api";
import { fmtDate, num } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { useAssistantOptional } from "@/context/AssistantContext";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { PageHeader } from "@/components/common/Page";
import { Panel } from "@/components/finance/FinanceBits";
import { EmptyState } from "@/components/common/States";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { CapacityBrief, CoverageSummary, SourcingPlan, SupportChecks, RiskList, Alternatives, Verdict, RequestLine, quoteUrlForPlan, noDate, noDateShort } from "@/components/capacity/CapacityBits";
import { OutlookChart } from "@/components/capacity/OutlookChart";
import { ReserveSheet } from "@/components/capacity/ReserveSheet";
import { BlocksPanel } from "@/components/capacity/BlocksPanel";

const MONTHS = [1, 2, 3, 4, 5, 6, 8, 9, 10, 12, 18, 24];

/** Default demo question: «1. desember» – this year if it is still ahead of us, otherwise next year. */
const defaultStart = () => {
  const t = new Date();
  const y = t.getMonth() >= 11 ? t.getFullYear() + 1 : t.getFullYear();
  return `${y}-12-01`;
};

const Field = ({ label, children, className, hint }) => (
  <div className={cn("min-w-0", className)}>
    <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</Label>
    <div className="mt-1.5">{children}</div>
    {hint && <p className="mt-1 text-[11px] leading-4 text-muted-foreground">{hint}</p>}
  </div>
);

const LensFacts = ({ o, className }) => {
  if (!o?.summary) return null;
  const s = o.summary;
  const facts = [
    { id: "physical", label: "Fysisk", value: s.physical, sub: `${s.modules} boligmoduler` },
    { id: "hard", label: "Kontraktsfestet", value: s.hard, sub: "hard last denne uken" },
    { id: "free", label: "Ledig for salg", value: s.free_hard, sub: s.lowest ? `lavest ${s.lowest.free_hard} i ${s.lowest.week}` : "", tone: "success" },
    { id: "soft", label: "Soft holds", value: s.soft_peak ?? s.soft, sub: s.soft_peak ? `topp i ${s.soft_peak_week} · vektet ${s.weighted_peak}` : "ingen aktive tilbud", tone: s.soft_peak ? "warning" : undefined },
    { id: "conflict", label: "Mangel hvis alle vinner", value: s.max_shortfall || 0, sub: s.conflict_weeks?.length ? `${s.conflict_weeks[0]}–${s.conflict_weeks[s.conflict_weeks.length - 1]}` : "ingen konflikt", tone: s.max_shortfall ? "danger" : "success" },
  ];
  return (
    <div className={cn("grid grid-cols-2 gap-x-5 gap-y-4 sm:grid-cols-5", className)} data-testid="capacity-lens-facts">
      {facts.map((f, i) => (
        <div key={f.id} className={cn("min-w-0", i > 0 && "sm:border-l sm:border-border/70 sm:pl-5")} data-testid={`capacity-lens-${f.id}`}>
          <div className={cn("font-mono text-[22px] font-semibold leading-7 tracking-tight tabular", f.tone === "danger" && "text-rose-600 dark:text-rose-400", f.tone === "warning" && "text-amber-700 dark:text-amber-300", f.tone === "success" && "text-emerald-700 dark:text-emerald-300")} data-testid={`capacity-lens-${f.id}-value`}>{num(f.value)}</div>
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{f.label}</div>
          {f.sub && <div className="truncate text-[11.5px] leading-4 text-muted-foreground/90">{f.sub}</div>}
        </div>
      ))}
    </div>
  );
};

export default function CapacityPage() {
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const { can } = useAuth();
  const assistant = useAssistantOptional();
  const canRead = can("planning:read");
  const canWrite = can("planning:write");
  const canLead = can("leads:write");
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const resultRef = useRef(null);

  const [form, setForm] = useState(() => ({
    persons: sp.get("persons") || "120",
    dest: sp.get("dest") || "",
    destName: sp.get("sted") || (sp.get("dest") || sp.get("plan") ? "" : "Mongstad"),
    start: sp.get("start") || defaultStart(),
    months: sp.get("months") || "6",
    extend_project_id: "",
    extend_weeks: "3",
    treat_soft_as_hard: false,
  }));
  const [plan, setPlan] = useState(null);
  const [reserveOpen, setReserveOpen] = useState(false);
  const [weekSheet, setWeekSheet] = useState(null);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));

  const { data: sites } = useQuery({ queryKey: ["capacity-sites"], queryFn: () => get("/capacity/sites"), enabled: canRead });
  const { data: outlook, isLoading: outlookLoading, isError: outlookError, refetch: refetchOutlook } = useQuery({ queryKey: ["capacity-outlook"], queryFn: () => get("/capacity/outlook", { weeks: 26 }), enabled: canRead });
  const { data: blocks } = useQuery({ queryKey: ["capacity-blocks"], queryFn: () => get("/capacity/blocks"), enabled: canRead });
  const projects = useMemo(() => (sites || []).filter((s) => s.kind === "project"), [sites]);
  const depots = useMemo(() => (sites || []).filter((s) => s.kind === "location"), [sites]);

  /* deep link ?plan=<id> (from assistant / shared link) */
  const planParam = sp.get("plan");
  useEffect(() => {
    if (!canRead || !planParam || (plan && (plan.id === planParam))) return;
    get(`/capacity/plans/${planParam}`)
      .then((p) => {
        setPlan(p);
        const r = p.request || {};
        setForm((f) => ({ ...f, persons: String(r.persons || f.persons), start: r.start_date || f.start, months: String(r.months || f.months), dest: r.destination?.project_id || r.destination?.location_id || (r.destination?.name ? "__other" : ""), destName: r.destination?.kind === "site" ? r.destination?.name || "" : f.destName,
          extend_project_id: r.scenario?.extend_project_id || "", extend_weeks: String(r.scenario?.extend_weeks || 3), treat_soft_as_hard: !!r.scenario?.treat_soft_as_hard }));
        if (sp.get("reserver") === "1") setReserveOpen(true);
      })
      .catch(() => toast.error("Fant ikke kapasitetsplanen", { description: "Kjør sjekken på nytt." }));
  }, [planParam]); // intentionally only re-runs on a new ?plan= id

  const check = useMutation({
    mutationFn: (override) => {
      const f = { ...form, ...(override || {}) };
      const site = (sites || []).find((s) => s.id === f.dest);
      const destination = site ? (site.kind === "project" ? { project_id: site.id, name: site.short } : { location_id: site.id }) : f.destName ? { name: f.destName } : null;
      const scenario = { extend_project_id: f.extend_project_id || null, extend_weeks: f.extend_project_id ? Number(f.extend_weeks || 0) : null, treat_soft_as_hard: f.treat_soft_as_hard || null };
      return post("/capacity/check", { persons: Number(f.persons), destination, start_date: f.start, months: Number(f.months), scenario });
    },
    onSuccess: (p) => {
      setPlan(p);
      const next = new URLSearchParams(sp);
      next.set("plan", p.id);
      ["persons", "start", "months", "dest", "sted", "reserver", "auto"].forEach((k) => next.delete(k));
      setSp(next, { replace: true });
      if (!isDesktop) setTimeout(() => resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
    },
    onError: (e) => toastError(e, "Kapasitetssjekken feilet"),
  });

  /* ?auto=1 from the assistant «Vis kapasitetsplan» with query prefilled but no plan id */
  useEffect(() => {
    if (sp.get("auto") === "1" && sites && !plan && !check.isPending) check.mutate();
  }, [sites]); // runs once the site list is available

  const runWithAlternative = (a) => {
    if (a.kind === "move_start" && a.date) { setForm((f) => ({ ...f, start: a.date })); check.mutate({ start: a.date }); }
    if (a.kind === "move_project" && a.project_id) { setForm((f) => ({ ...f, extend_project_id: a.project_id, extend_weeks: String(-Math.ceil((a.days || 0) / 7)) })); check.mutate({ extend_project_id: a.project_id, extend_weeks: String(-Math.ceil((a.days || 0) / 7)) }); }
  };

  const whatIfActive = !!form.extend_project_id || form.treat_soft_as_hard;
  const chartWeeks = plan?.outlook || outlook?.weeks || [];
  const planRange = plan ? [plan.outlook?.find((w) => w.need > 0)?.week, [...(plan.outlook || [])].reverse().find((w) => w.need > 0)?.week] : null;
  const softBlocks = useMemo(() => (blocks || []).filter((b) => b.kind === "soft" && b.status === "aktiv"), [blocks]);
  const weekConflicts = weekSheet ? softBlocks.filter((b) => b.start_date < weekSheet.end && b.end_date > weekSheet.start) : [];
  const subtitle = outlook?.brief?.[0] || "Kan vi levere? Motoren regner på faktisk flåte, allokeringer, ledetider og aktive tilbud.";

  const askAssistant = () => {
    if (!assistant) return;
    assistant.open?.("dock");
    assistant.setDraft?.(plan ? "Forklar denne kapasitetsplanen – hva er risikoen og hva bør vi gjøre?" : `Kan vi levere ${form.persons} personer ${form.destName ? `på ${form.destName} ` : ""}fra ${noDate(form.start)} i ${form.months} måneder?`);
  };

  const actions = (
    <>
      {canWrite && <Button onClick={() => setReserveOpen(true)} disabled={!plan} data-testid="capacity-plan-reserve-button"><CalendarCheck className="h-4 w-4" /> Reserver kapasitet</Button>}
      {canLead && <Button variant="secondary" disabled={!plan} onClick={() => navigate(quoteUrlForPlan(plan))} data-testid="capacity-plan-create-quote-button"><FileText className="h-4 w-4" /> Lag tilbud</Button>}
      {assistant && <Button variant="ghost" onClick={askAssistant} data-testid="capacity-ask-assistant-button"><Sparkles className="h-4 w-4 text-primary" /> Spør assistenten</Button>}
    </>
  );

  if (!canRead) {
    return (
      <div className="page-pad" data-testid="capacity-page">
        <PageHeader eyebrow="Planlegging" title="Kapasitet" testId="capacity-header" />
        <EmptyState icon={Lock} title="Ingen tilgang til kapasitetsplanlegging" description="Rollen din har ikke lesetilgang til planlegging. Kontakt administrator hvis du trenger kapasitetsbildet." testId="capacity-no-access" className="py-20" />
      </div>
    );
  }

  return (
    <div className="page-pad pb-28 lg:pb-12" data-testid="capacity-page">
      <div className="fade-up">
      <PageHeader
        eyebrow={<span className="inline-flex items-center gap-2">Planlegging <span aria-hidden>·</span> <Link to="/planlegging" className="inline-flex items-center gap-1 hover:text-foreground" data-testid="capacity-to-timeline-link"><GanttChart className="h-3.5 w-3.5" /> Tidslinje</Link></span>}
        title="Kapasitet"
        subtitle={subtitle}
        actions={<div className="hidden items-center gap-2 lg:flex">{actions}</div>}
        testId="capacity-header"
      />

      <div className="mt-6">
        {outlookLoading && <div className="grid grid-cols-2 gap-5 sm:grid-cols-5" data-testid="capacity-loading">{[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-14" />)}</div>}
        {outlookError && (
          <div className="flex items-center justify-between rounded-lg bg-rose-500/[0.06] px-3 py-2 text-[12.5px] text-rose-700 dark:text-rose-300" data-testid="capacity-error-alert">
            Kunne ikke laste kapasitetsutsikten. <Button size="sm" variant="ghost" onClick={() => refetchOutlook()} data-testid="capacity-outlook-retry-button">Prøv igjen</Button>
          </div>
        )}
        {outlook && <LensFacts o={outlook} />}
      </div>

      <div className="mt-10 grid gap-10 lg:grid-cols-12 lg:gap-12">
        {/* ---------------------------------------------------------------- main ---- */}
        <div className="min-w-0 space-y-12 lg:col-span-8">
          <Panel title="Kapasitetssjekk" meta="Still spørsmålet – motoren finner en forklarbar sourcingplan. Ingenting skrives før du reserverer." testId="capacity-check-panel">
            <form
              className="grid grid-cols-2 gap-4 sm:grid-cols-4"
              data-testid="capacity-check-form"
              onSubmit={(e) => { e.preventDefault(); if (Number(form.persons) > 0 && form.start) check.mutate(); }}
            >
              <Field label="Personer">
                <div className="relative">
                  <Input type="number" min={1} max={2000} value={form.persons} onChange={set("persons")} className="pr-16 font-mono tabular" data-testid="capacity-check-persons-input" />
                  <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[11px] text-muted-foreground">personer</span>
                </div>
              </Field>
              <Field label="Sted" className="col-span-2 sm:col-span-1">
                <Select value={form.dest || "__other"} onValueChange={(v) => setForm((f) => ({ ...f, dest: v === "__other" ? "" : v }))}>
                  <SelectTrigger data-testid="capacity-check-location-select"><SelectValue placeholder="Velg sted" /></SelectTrigger>
                  <SelectContent className="max-h-80">
                    <SelectItem value="__other">Nytt sted (skriv under)</SelectItem>
                    <SelectGroup>
                      <SelectLabel>Prosjekter</SelectLabel>
                      {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                    </SelectGroup>
                    <SelectGroup>
                      <SelectLabel>Depoter</SelectLabel>
                      {depots.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                    </SelectGroup>
                  </SelectContent>
                </Select>
                {!form.dest && <Input className="mt-2" placeholder="F.eks. Mongstad" value={form.destName} onChange={set("destName")} data-testid="capacity-check-location-input" />}
              </Field>
              <Field label="Fra">
                <Input type="date" value={form.start} onChange={set("start")} className="font-mono tabular" data-testid="capacity-check-start-date-button" />
              </Field>
              <Field label="Varighet">
                <Select value={String(form.months)} onValueChange={set("months")}>
                  <SelectTrigger data-testid="capacity-check-months-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{MONTHS.map((m) => <SelectItem key={m} value={String(m)}>{m} {m === 1 ? "måned" : "måneder"}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <div className="col-span-2 flex flex-wrap items-center gap-3 sm:col-span-4">
                <Button type="submit" disabled={check.isPending || !(Number(form.persons) > 0)} data-testid="capacity-check-compute-button">
                  {check.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />} {plan ? "Regn på nytt" : "Sjekk kapasitet"}
                </Button>
                {whatIfActive && <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-500/10 px-2.5 py-1 text-[11.5px] text-amber-800 dark:text-amber-300" data-testid="capacity-what-if-badge">what-if aktiv · skriver ikke</span>}
                <span className="text-[11.5px] text-muted-foreground">Til {form.start ? noDate(fmtDate(new Date(new Date(form.start).setMonth(new Date(form.start).getMonth() + Number(form.months || 0))), "yyyy-MM-dd")) : "–"}</span>
              </div>
            </form>
          </Panel>

          {check.isPending && !plan && (
            <div className="space-y-4" data-testid="capacity-result-loading"><Skeleton className="h-8 w-2/3" /><Skeleton className="h-20" /><Skeleton className="h-40" /></div>
          )}

          {plan && (
            <div ref={resultRef} className="space-y-12 scroll-mt-20">
              <Panel title="Svar" meta={<RequestLine request={plan.request} />} testId="capacity-answer-panel">
                <Verdict plan={plan} />
                <CoverageSummary plan={plan} className="mt-6" />
                <CapacityBrief sentences={plan.brief.slice(1)} className="mt-6" compact meta={null} testId="capacity-plan-brief" />
              </Panel>

              <Panel title="Sourcingplan" meta={`${plan.summary.modules} moduler i ${plan.phases.length} ${plan.phases.length === 1 ? "fase" : "faser"} · klar-dato er på destinasjonen, etter demontering → klargjøring → transport → montering`} testId="capacity-sourcing-panel">
                <SourcingPlan plan={plan} />
                <div className="mt-5 border-t border-border/70 pt-4">
                  <div className="mb-2 text-[11px] uppercase tracking-wide text-muted-foreground">Støttekapasitet</div>
                  <SupportChecks support={plan.support} />
                </div>
              </Panel>
            </div>
          )}

          <Panel
            title="Kapasitetsutsikt"
            meta={plan ? "Hele flåten uke for uke – med denne planen lagt på toppen av kontraktsfestet last." : "Hele flåten uke for uke i fire perspektiver. Soft og vektet er eksponering, ikke ledig kapasitet."}
            action={<span className="text-[11.5px] text-muted-foreground tabular">{chartWeeks.length} uker · klikk en uke for detaljer</span>}
            testId="capacity-outlook-panel"
          >
            <OutlookChart weeks={chartWeeks} hasPlan={!!plan} onWeekClick={setWeekSheet} highlightRange={planRange?.[0] ? planRange : null} minChartWidth={isDesktop ? 0 : 640} height={isDesktop ? 240 : 190} />
            {outlook?.brief?.length > 1 && !plan && <CapacityBrief sentences={outlook.brief.slice(1)} compact meta={null} className="mt-4" testId="capacity-outlook-brief" />}
          </Panel>

          <Panel title="Blokker" meta="Soft holds teller som eksponering, kontraktsfestede som hard last. Begge kan frigis – alt logges." testId="capacity-blocks-panel">
            <BlocksPanel canWrite={canWrite} />
          </Panel>
        </div>

        {/* ---------------------------------------------------------------- aside ---- */}
        <aside className="min-w-0 space-y-10 lg:col-span-4 lg:border-l lg:border-border/70 lg:pl-10">
          <Panel title="What-if" meta="Endre forutsetninger og regn på nytt. Skriver aldri til databasen." testId="capacity-what-if-panel">
            <div className="space-y-4">
              <Field label="Forleng / forkort prosjekt">
                <Select value={form.extend_project_id || "__none"} onValueChange={(v) => setForm((f) => ({ ...f, extend_project_id: v === "__none" ? "" : v }))}>
                  <SelectTrigger data-testid="capacity-what-if-project-select"><SelectValue placeholder="Ingen" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="__none">Ingen</SelectItem>
                    {projects.filter((p) => p.status !== "planlegging").map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              {form.extend_project_id && (
                <Field label="Uker (negativt = avslutt tidligere)">
                  <Input type="number" min={-26} max={52} value={form.extend_weeks} onChange={set("extend_weeks")} className="font-mono tabular" data-testid="capacity-what-if-extend-weeks-input" />
                </Field>
              )}
              <div className="flex items-center justify-between gap-3 rounded-lg bg-muted/50 px-3 py-2.5">
                <div>
                  <div className="text-[12.5px] font-medium">Som om alle tilbud vinner</div>
                  <div className="text-[11.5px] text-muted-foreground">Behandler aktive soft holds som hard last.</div>
                </div>
                <Switch checked={form.treat_soft_as_hard} onCheckedChange={set("treat_soft_as_hard")} data-testid="capacity-what-if-soft-as-hard-switch" />
              </div>
              <div className="flex items-center gap-2">
                <Button variant="secondary" size="sm" onClick={() => check.mutate()} disabled={check.isPending} data-testid="capacity-what-if-run-button"><TrendingUp className="h-4 w-4" /> Regn med what-if</Button>
                {whatIfActive && <Button variant="ghost" size="sm" onClick={() => setForm((f) => ({ ...f, extend_project_id: "", extend_weeks: "3", treat_soft_as_hard: false }))} data-testid="capacity-what-if-reset-button"><RotateCcw className="h-3.5 w-3.5" /> Nullstill</Button>}
              </div>
            </div>
          </Panel>

          <Panel title="Risiko" meta={plan ? `${plan.risks.length} ${plan.risks.length === 1 ? "punkt" : "punkter"} for denne planen` : "Vises når en plan er beregnet"} testId="capacity-risk-panel">
            {plan ? <RiskList risks={plan.risks} /> : (
              <div className="flex items-start gap-2.5 text-[12.5px] leading-5 text-muted-foreground"><AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" strokeWidth={1.75} /> Soft holds som overlapper, konfliktscenario og manglende støttekapasitet vises her.</div>
            )}
          </Panel>

          {plan?.alternatives?.length > 0 && (
            <Panel title="Alternativer" meta="Forslag fra motoren – klikk for å regne på dem" testId="capacity-alternatives-panel">
              <Alternatives items={plan.alternatives} onPick={runWithAlternative} />
            </Panel>
          )}

          <Panel title="Slik regner motoren" testId="capacity-method-panel">
            <ul className="space-y-2 text-[12.5px] leading-5 text-muted-foreground">
              <li className="flex gap-2"><Layers className="mt-0.5 h-3.5 w-3.5 shrink-0" strokeWidth={1.75} /> Hver modul vurderes med faktisk allokering, hvor den står og når den blir fri.</li>
              <li className="flex gap-2"><ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0" strokeWidth={1.75} /> Klar-dato på destinasjonen = fri + demontering + klargjøring + transport + montering (ledetider per kategori, overstyres av faktisk logistikk).</li>
              <li className="flex gap-2"><Lightbulb className="mt-0.5 h-3.5 w-3.5 shrink-0" strokeWidth={1.75} /> Færrest kilder først, så minst håndtering. Soft holds er aldri «ledig», men vises som eksponering og konflikt.</li>
            </ul>
            {plan?.lead_times?.bolig && <div className="mt-3 font-mono text-[11px] tabular text-muted-foreground" data-testid="capacity-lead-times">bolig: {Object.entries(plan.lead_times.bolig).map(([k, v]) => `${k} ${v}d`).join(" · ")}</div>}
          </Panel>
        </aside>
      </div>

      </div>

      {/* mobile sticky actions (outside the animated wrapper – a transform would break position:fixed) */}
      <div className="fixed inset-x-0 bottom-0 z-30 flex items-center gap-2 border-t border-border/70 bg-card/90 px-4 py-3 backdrop-blur-md lg:hidden" data-testid="capacity-mobile-actions">
        {canWrite && <Button className="flex-1" onClick={() => setReserveOpen(true)} disabled={!plan} data-testid="capacity-plan-reserve-button-mobile"><CalendarCheck className="h-4 w-4" /> Reserver</Button>}
        {canLead && <Button variant="secondary" className="flex-1" disabled={!plan} onClick={() => navigate(quoteUrlForPlan(plan))} data-testid="capacity-plan-create-quote-button-mobile"><FileText className="h-4 w-4" /> Lag tilbud</Button>}
      </div>

      <ReserveSheet plan={plan} open={reserveOpen} onOpenChange={(o) => { setReserveOpen(o); if (!o && sp.get("reserver")) { const n = new URLSearchParams(sp); n.delete("reserver"); setSp(n, { replace: true }); } }} />

      <Sheet open={!!weekSheet} onOpenChange={(o) => !o && setWeekSheet(null)}>
        <SheetContent className="w-[440px] max-w-[94vw] overflow-y-auto" data-testid="capacity-week-sheet">
          {weekSheet && (
            <>
              <SheetHeader>
                <SheetTitle className="font-mono">{weekSheet.week} <span className="text-muted-foreground">· {noDateShort(weekSheet.start)} – {noDateShort(weekSheet.end)}</span></SheetTitle>
                <SheetDescription>Fire perspektiver på samme uke.</SheetDescription>
              </SheetHeader>
              <dl className="mt-5 grid grid-cols-[1fr_auto] gap-y-2 text-[13px]">
                {[["Fysisk kapasitet", weekSheet.physical], ["Kontraktsfestet (hard)", weekSheet.hard], ["Ledig (hard)", weekSheet.free_hard], plan ? ["Denne planen", weekSheet.plan] : null, plan ? ["Ledig etter plan", weekSheet.free_after_plan] : null, ["Soft holds", weekSheet.soft], ["Vektet pipeline", weekSheet.weighted], ["Mangel hvis alle vinner", plan ? weekSheet.shortfall_after_plan : weekSheet.shortfall]].filter(Boolean).map(([k, v]) => (
                  <div key={k} className="contents">
                    <dt className="text-muted-foreground">{k}</dt>
                    <dd className={cn("font-mono tabular text-right", k.startsWith("Mangel") && v > 0 && "text-rose-600 dark:text-rose-400")}>{v ?? 0}</dd>
                  </div>
                ))}
              </dl>
              {weekSheet.hard_by_type && Object.keys(weekSheet.hard_by_type).length > 0 && (
                <div className="mt-4 text-[12px] text-muted-foreground">Hard last: {Object.entries(weekSheet.hard_by_type).map(([k, v]) => `${k} ${v}`).join(" · ")}</div>
              )}
              <div className="mt-6">
                <div className="mb-2 text-[11px] uppercase tracking-wide text-muted-foreground">Aktive tilbud i uken</div>
                {weekConflicts.length ? (
                  <ul className="divide-y divide-border/70 text-[12.5px]" data-testid="capacity-week-conflicts">
                    {weekConflicts.map((b) => (
                      <li key={b.id} className="flex items-center justify-between gap-3 py-2">
                        <span className="min-w-0 truncate">{b.title}</span>
                        <span className="shrink-0 font-mono tabular text-muted-foreground">{b.units} · {Math.round((b.probability || 0) * 100)} %</span>
                      </li>
                    ))}
                  </ul>
                ) : <div className="text-[12.5px] text-muted-foreground">Ingen soft holds denne uken.</div>}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
